import { useQuery } from '@tanstack/react-query';
import { Truck, FileText } from 'lucide-react';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/dateFmt';

interface HandoverSummary {
  id: string;
  station: string | null;
  registration: string | null;
  created_at: string;
}

/**
 * The inductee's signed kit-handover checklists (vehicle handovers etc.),
 * each downloadable as the signed PDF, like their other signed documents.
 * Renders nothing when they have none.
 */
export function MyHandoversCard() {
  const list = useQuery({
    queryKey: ['my-handovers'],
    queryFn: async () =>
      (await api<{ submissions: HandoverSummary[] }>('/api/kit-handover/mine')).submissions,
  });

  const submissions = list.data ?? [];
  if (submissions.length === 0) return null;

  return (
    <section className="overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm">
      <header className="flex items-center gap-2 border-b border-divider/10 px-5 py-3">
        <Truck className="h-3.5 w-3.5 text-accent" />
        <span className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
          Kit handovers
        </span>
      </header>
      <ul className="divide-y divide-divider/10">
        {submissions.map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-3 px-5 py-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-fg">
                {s.station ?? 'Kit'} handover
                {s.registration ? ` · ${s.registration}` : ''}
              </div>
              <div className="text-xs text-fg-muted">{formatDate(s.created_at)}</div>
            </div>
            <a
              href={`/api/kit-handover/submissions/${s.id}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-token-sm px-2 text-xs font-medium text-accent transition-colors hover:bg-accent/10"
            >
              <FileText className="h-3.5 w-3.5" />
              View signed PDF
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
